<template>
    <div class="srv-pdc">
        <header class="srv-head">
            <div class="srv-avatar">{{ initiales }}</div>
            <div>
                <div class="srv-head-titre">Point de caisse</div>
                <div class="srv-head-sous">{{ dateDuJour }} · {{ serveur.name }}</div>
            </div>
        </header>

        <div class="srv-corps">
            <!-- La maquette annonçait « Montant total collecté ». C'était faux :
                 ce chiffre additionne AUSSI les commandes ouvertes et celles en
                 attente de scan, qui ne sont pas encaissées. Sur un écran où le
                 serveur compte ses espèces, un libellé inexact vaut une erreur
                 de caisse - on nomme donc le total pour ce qu'il est, et on
                 montre à part ce qui reste dû. -->
            <div class="srv-hero srv-carte">
                <span class="srv-hero-lbl">Total de mes commandes</span>
                <span class="srv-hero-val srv-num">{{ fmt(totalGeneral) }}</span>
                <div v-if="resteAEncaisser > 0" class="srv-hero-reste">
                    <i class="ri-time-line"></i>
                    <span>Reste à encaisser</span>
                    <span class="srv-num">{{ fmt(resteAEncaisser) }}</span>
                </div>
            </div>

            <!-- L'encaissement est mixte : le serveur doit voir d'un coup d'œil
                 ce qui est passé en caisse et ce dont il répond lui-même. -->
            <div class="srv-split">
                <div class="srv-split-c srv-carte">
                    <span class="srv-split-lbl">Encaissé en caisse</span>
                    <span class="srv-split-val srv-num">{{ fmt(totalCaisse) }}</span>
                    <span class="srv-split-nb">{{ nbCaisse }} commande{{ nbCaisse > 1 ? 's' : '' }}</span>
                </div>
                <div class="srv-split-c srv-carte">
                    <span class="srv-split-lbl">Encaissé par moi</span>
                    <span class="srv-split-val srv-num">{{ fmt(totalMoi) }}</span>
                    <span class="srv-split-nb">{{ nbMoi }} commande{{ nbMoi > 1 ? 's' : '' }}</span>
                </div>
            </div>

            <div class="srv-decl srv-carte">
                <h2 class="srv-decl-titre">Déclaration d'espèces</h2>

                <div class="srv-decl-l">
                    <span>Fond de caisse</span>
                    <span class="srv-num">{{ fmt(fondDeCaisse) }}</span>
                </div>

                <label class="srv-decl-l srv-decl-saisie">
                    <span>Espèces collectées</span>
                    <input v-model.number="especes" type="number" min="0" step="100"
                           inputmode="numeric" class="srv-num" />
                </label>

                <!-- L'écart est LA ligne que le manager regarde. Vert à zéro,
                     rouge s'il manque, ambre s'il y a trop : trois états, pas
                     un simple « ok / pas ok ». -->
                <div class="srv-decl-l srv-ecart" :class="tonEcart">
                    <span>Écart de caisse</span>
                    <span class="srv-ecart-val srv-num">
                        <i :class="iconeEcart"></i>
                        {{ ecart > 0 ? '+' : '' }}{{ fmt(ecart) }}
                    </span>
                </div>
            </div>

            <h2 class="srv-sec-titre">Détail des transactions</h2>

            <div v-if="!commandes.length" class="srv-vide">
                <i class="ri-file-list-3-line"></i>
                <p>Aucune transaction</p>
            </div>

            <!-- Le tableau défile DANS son conteneur : la page, elle, ne doit
                 jamais partir de côté (la maquette Stitch coupait « MONTANT »). -->
            <div v-else class="srv-tab-wrap">
                <table class="srv-tab">
                    <thead>
                        <tr>
                            <th>Table</th><th>N° attente</th><th class="c">Art.</th>
                            <th class="d">Montant</th><th>Règlement</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in commandes" :key="c.id">
                            <td class="srv-tab-table">
                                {{ c.table ? c.table.name : '-' }}
                                <small v-if="labelClient(c)" class="srv-tab-client">{{ labelClient(c) }}</small>
                            </td>
                            <td class="srv-num">{{ c.numero }}</td>
                            <td class="c srv-num">{{ nbArticles(c) }}</td>
                            <td class="d srv-num">{{ fmt(totalCommande(c)) }}</td>
                            <td>
                                <span class="srv-pill" :class="'srv-pill-' + STATUTS[c.statut].ton">
                                    {{ STATUTS[c.statut].libelle }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <button class="srv-btn srv-cloturer" @click="demanderCloture = true">
                <i class="ri-restart-line"></i>
                <span>Clôturer mon service</span>
            </button>
        </div>

        <!-- Confirmation : la clôture récapitule les chiffres avant validation. -->
        <div v-if="demanderCloture" class="srv-modal-overlay" @click.self="demanderCloture = false">
            <div class="srv-modal">
                <h2>Clôturer le service ?</h2>
                <div class="srv-modal-l"><span>Commandes</span><span class="srv-num">{{ commandes.length }}</span></div>
                <div class="srv-modal-l"><span>Total collecté</span><span class="srv-num">{{ fmt(totalGeneral) }}</span></div>
                <div class="srv-modal-l"><span>Dont par moi</span><span class="srv-num">{{ fmt(totalMoi) }}</span></div>
                <div class="srv-modal-l" :class="tonEcart">
                    <span>Écart de caisse</span>
                    <span class="srv-num">{{ ecart > 0 ? '+' : '' }}{{ fmt(ecart) }}</span>
                </div>
                <p v-if="ecart !== 0" class="srv-modal-avert">
                    <i class="ri-error-warning-line"></i>
                    L'écart n'est pas nul. Vérifiez vos espèces avant de clôturer.
                </p>
                <div class="srv-modal-actions">
                    <button class="srv-btn srv-btn-sec" @click="demanderCloture = false">Annuler</button>
                    <button class="srv-btn" @click="confirmer">Confirmer</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    import { fmt } from "../menu/format";
    import { STATUTS, labelClient } from "./statuts";

    export default {
        name: "srvpointdecaisse",
        props: {
            serveur: { type: Object, required: true },
            commandes: { type: Array, default: () => [] },
            fondDeCaisse: { type: Number, default: 0 },
        },
        emits: ["cloturer"],
        data() {
            return { STATUTS, especes: 0, demanderCloture: false };
        },
        computed: {
            initiales() {
                return String(this.serveur.name || "")
                    .split(/\s+/).filter(Boolean).slice(0, 2)
                    .map((m) => m[0]).join("").toUpperCase();
            },
            dateDuJour() {
                return new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
            },
            totalGeneral() { return this.commandes.reduce((s, c) => s + this.totalCommande(c), 0); },
            parStatut() {
                const r = { caisse: [], moi: [], attente: [], verification: [], en_service: [], ouverte: [] };
                for (const c of this.commandes) (r[c.statut] || r.attente).push(c);
                return r;
            },
            /** Ouvertes, en service, en attente ou en vérification : rien de confirmé réglé. */
            resteAEncaisser() {
                return [].concat(this.parStatut.ouverte, this.parStatut.en_service,
                                 this.parStatut.attente, this.parStatut.verification)
                    .reduce((s, c) => s + this.totalCommande(c), 0);
            },
            totalCaisse() { return this.parStatut.caisse.reduce((s, c) => s + this.totalCommande(c), 0); },
            totalMoi() { return this.parStatut.moi.reduce((s, c) => s + this.totalCommande(c), 0); },
            nbCaisse() { return this.parStatut.caisse.length; },
            nbMoi() { return this.parStatut.moi.length; },

            // Ce que le serveur doit avoir en main = fond + ce qu'il a encaissé.
            attendu() { return this.fondDeCaisse + this.totalMoi; },
            ecart() { return (Number(this.especes) || 0) - this.attendu; },
            tonEcart() {
                if (this.ecart === 0) return "ok";
                return this.ecart < 0 ? "manque" : "excedent";
            },
            iconeEcart() {
                if (this.ecart === 0) return "ri-checkbox-circle-line";
                return this.ecart < 0 ? "ri-error-warning-line" : "ri-information-line";
            },
        },
        methods: {
            fmt,
            labelClient,
            totalCommande(c) { return c.lignes.reduce((s, l) => s + l.pu * l.qte, 0); },
            nbArticles(c) { return c.lignes.reduce((s, l) => s + l.qte, 0); },
            confirmer() { this.demanderCloture = false; this.$emit("cloturer"); },
        },
        mounted() {
            // Pré-remplissage sur le montant attendu : le cas nominal est
            // l'écart nul, autant ne pas faire retaper la somme.
            this.especes = this.attendu;
        },
    };
</script>

<style scoped>
    .srv-pdc { display: flex; flex-direction: column; min-height: 100vh; }

    .srv-avatar {
        width: 38px; height: 38px; border-radius: var(--srv-r-full);
        display: flex; align-items: center; justify-content: center;
        background: var(--srv-primary); color: var(--srv-on-primary);
        font-size: 14px; font-weight: 800; text-transform: uppercase; flex-shrink: 0;
    }

    .srv-hero { padding: var(--srv-md); margin-bottom: var(--srv-sm); }
    .srv-hero-lbl {
        display: block; font-size: 12px; font-weight: 700;
        letter-spacing: .05em; text-transform: uppercase; color: var(--srv-on-variant);
    }
    .srv-hero-val { display: block; margin-top: 6px; font-size: 30px; font-weight: 800; color: var(--srv-primary); }
    .srv-hero-reste {
        display: flex; align-items: center; gap: 8px;
        margin-top: var(--srv-sm); padding-top: var(--srv-sm);
        border-top: 1px solid var(--srv-outline-variant);
        font-size: 14px; color: var(--srv-on-variant);
    }
    .srv-hero-reste i { font-size: 16px; }
    .srv-hero-reste > span:last-child { margin-left: auto; font-weight: 800; color: var(--srv-info); }

    .srv-split { display: grid; grid-template-columns: 1fr 1fr; gap: var(--srv-sm); }
    .srv-split-c { padding: var(--srv-md); min-width: 0; }
    .srv-split-lbl {
        display: block; font-size: 11px; font-weight: 700;
        letter-spacing: .04em; text-transform: uppercase; color: var(--srv-on-variant);
    }
    .srv-split-val { display: block; margin-top: 6px; font-size: 18px; font-weight: 800; overflow-wrap: anywhere; }
    .srv-split-nb { display: block; margin-top: 2px; font-size: 13px; color: var(--srv-success); }

    .srv-decl { margin-top: var(--srv-md); padding: var(--srv-md); }
    .srv-decl-titre { margin: 0 0 var(--srv-sm); font-size: 16px; font-weight: 700; }
    .srv-decl-l {
        display: flex; align-items: center; justify-content: space-between; gap: var(--srv-sm);
        min-height: 44px; font-size: 15px; color: var(--srv-on-variant);
    }
    .srv-decl-l > span:last-child { font-weight: 700; color: var(--srv-on); }

    .srv-decl-saisie input {
        width: 130px; padding: 8px 10px; text-align: right;
        background: var(--srv-surface); color: var(--srv-on);
        border: 1px solid var(--srv-border-fort); border-radius: var(--srv-r-input);
        font-family: inherit; font-size: 16px; font-weight: 700; outline: none;
    }
    .srv-decl-saisie input:focus { border-color: var(--srv-primary); }

    .srv-ecart {
        margin-top: var(--srv-sm); padding-top: var(--srv-sm);
        border-top: 1px solid var(--srv-outline-variant);
        font-size: 16px; font-weight: 700; color: var(--srv-on);
    }
    .srv-ecart-val { display: flex; align-items: center; gap: 6px; }
    /* Trois états DISTINCTS. L'excédent était en --srv-primary : sur la charte
       verte, il devenait indiscernable de l'écart nul - or « il manque » et
       « il y a trop » n'appellent pas la même vérification. */
    .srv-ecart.ok        .srv-ecart-val { color: var(--srv-success); }
    .srv-ecart.manque    .srv-ecart-val { color: var(--srv-error); }
    .srv-ecart.excedent  .srv-ecart-val { color: var(--srv-warn-on); }

    .srv-sec-titre {
        margin: var(--srv-lg) 0 var(--srv-sm);
        font-size: 12px; font-weight: 700; letter-spacing: .05em;
        text-transform: uppercase; color: var(--srv-on-variant);
    }

    .srv-tab-wrap {
        overflow-x: auto; -webkit-overflow-scrolling: touch;
        border: 1px solid var(--srv-outline-variant); border-radius: var(--srv-r);
        background: var(--srv-surface);
    }
    .srv-tab { width: 100%; border-collapse: collapse; font-size: 14px; }
    .srv-tab th {
        padding: 10px 12px; text-align: left; white-space: nowrap;
        background: var(--srv-surface-high); color: var(--srv-on-variant);
        font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    }
    .srv-tab td {
        padding: 12px; white-space: nowrap;
        border-top: 1px solid var(--srv-outline-variant);
    }
    .srv-tab .c { text-align: center; }
    .srv-tab .d { text-align: right; font-weight: 800; }
    .srv-tab-table { color: var(--srv-primary); font-weight: 700; }
    .srv-tab-client { display: block; margin-top: 2px; font-size: 11px; font-weight: 600; color: var(--srv-on-variant); }

    .srv-cloturer { margin-top: var(--srv-lg); }

    .srv-modal-overlay {
        position: fixed; inset: 0; z-index: 95;
        background: rgba(0, 0, 0, .65);
        display: flex; align-items: center; justify-content: center; padding: var(--srv-md);
    }
    .srv-modal {
        width: 100%; max-width: 400px; padding: var(--srv-lg);
        background: var(--srv-surface-low);
        border: 1px solid var(--srv-outline-variant); border-radius: 12px;
    }
    .srv-modal h2 { margin: 0 0 var(--srv-md); font-size: 19px; font-weight: 700; }
    .srv-modal-l {
        display: flex; align-items: center; justify-content: space-between;
        min-height: 38px; font-size: 15px; color: var(--srv-on-variant);
    }
    .srv-modal-l > span:last-child { font-weight: 700; color: var(--srv-on); }
    .srv-modal-l.ok       > span:last-child { color: var(--srv-success); }
    .srv-modal-l.manque   > span:last-child { color: var(--srv-error); }
    .srv-modal-l.excedent > span:last-child { color: var(--srv-warn-on); }

    .srv-modal-avert {
        display: flex; align-items: flex-start; gap: 8px;
        margin: var(--srv-sm) 0 0; padding: 10px;
        background: var(--srv-error-soft); border-radius: var(--srv-r-input);
        font-size: 13px; color: var(--srv-error);
    }
    .srv-modal-actions { display: flex; gap: var(--srv-sm); margin-top: var(--srv-md); }
</style>
